import React, { PropsWithChildren, useMemo } from 'react';
import { Checkbox, Box, Tooltip, Skeleton, Flex } from '@landr/maestro';
import { t } from '@lingui/macro';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import { Sample } from 'types/generated/graphql';
import { useTakenDownSample } from 'hooks/useTakenDown/useTakenDown';
import { useDownloadsPageMatch } from 'hooks/useDownloadsPageMatch/useDownloadsPageMatch';
import { defaultRowContainer } from '../SamplesTablePlayer/SamplesTablePlayer.styled';

interface CheckboxProps {
    sample: Sample;
    onClick?: (event: React.MouseEvent) => void;
}

const CheckboxWrapper: React.FC<
    PropsWithChildren<{ isDisabled?: boolean }>
> = ({ isDisabled = false, children }) =>
    isDisabled ? (
        <Tooltip label={t`Feature is unavailable`}>{children}</Tooltip>
    ) : (
        <>{children}</>
    );

export const SamplesTableCheckboxSkeleton = () => (
    <Flex className={defaultRowContainer}>
        <Flex width="xs">
            <Skeleton width="2xs" height="2xs" />
        </Flex>
    </Flex>
);

export const SamplesTableCheckbox: React.FC<CheckboxProps> = ({
    sample,
    onClick,
}) => {
    const { isSampleSelected, toggleSampleSelection } =
        useSelectedSamplesContext();

    const onChange = () => {
        toggleSampleSelection(sample);
    };

    const isSelected = useMemo(
        () => isSampleSelected(sample),
        [isSampleSelected, sample],
    );

    const { isTakenDown } = useTakenDownSample(sample);
    const { isDownloadsPage } = useDownloadsPageMatch();
    const isTakenDownUIVisible = isTakenDown && isDownloadsPage;

    return (
        <CheckboxWrapper isDisabled={isTakenDownUIVisible}>
            <Box display="flex" alignItems="center" onClick={onClick}>
                <Checkbox
                    role="checkbox"
                    aria-checked={isSelected}
                    data-testid={`row-checkbox-${sample.guid}`}
                    onChange={onChange}
                    isChecked={isSelected}
                    label={t`Select ${sample.name}`}
                    hideLabel
                    isDisabled={isTakenDownUIVisible}
                    size="sm"
                />
            </Box>
        </CheckboxWrapper>
    );
};
